/**
 * @file paddle 客户案例 案例列表 hooks
 * @author FengGuang(fengguang01@baidu.com)
 */

import {useCallback, useEffect, useState} from 'react';
import {useLatest} from 'react-use';

import {
    fetchGetEnterpriseCaseListData
} from '../../../../api/pages/customerCaseApi';
import {
    ICaseListItem,
    IIndustryListItem, IPagination,
    ITechListItem
} from './types';

interface IGetCaseListParams {
    page?: number;
    industry?: string;
    tech?: string;
    clickType?: string;
}

export const useDeveloperCaseListHooks = () => {
    const [industryList, setIndustryList] = useState<IIndustryListItem[]>([]);
    const [selectedIndustryKey, setSelectedIndustryKey] = useState<string>('0');
    const [techList, setTechList] = useState<ITechListItem[]>([]);
    const [selectedTechKey, setSelectedTechKey] = useState<string>('0');
    const [caseList, setCaseList] = useState<ICaseListItem[]>([]);
    const [pagination, setPagination] = useState<IPagination>({
        current: 1,
        pageSize: 9,
        total: 0
    });
    const [clickType, setClickType] = useState<'industry' | 'tech' | ''>('');

    const getCaseList = useCallback(async (params: IGetCaseListParams = {}) => {
        const theParam: IGetCaseListParams = {
            page: pagination.current,
            industry: selectedIndustryKey,
            tech: selectedTechKey,
            clickType: clickType,
            ...params
        };

        const res = await fetchGetEnterpriseCaseListData({
            categoryId: 3,
            page: theParam.page,
            industry: theParam.industry,
            tech: theParam.tech,
            clickType: theParam.industry === '0' && theParam.tech === '0' ? '' : theParam.clickType,
            schoolCaseSort: 2
        }).catch();

        if (res) {
            const pageData = res.body.result || {};
            const data = pageData.data || {};
            const ilist: any[] = Array.isArray(data.industryList) ? data.industryList : [];
            setIndustryList((old) => {
                if (old.length > 0) {
                    return old;
                }
                return ilist.map(item => ({
                    id: '' + item.id,
                    name: item.name
                }));
            });

            const tlist: any[] = Array.isArray(data.techList) ? data.techList : [];
            setTechList((old) => {
                if (old.length > 0) {
                    return old;
                }
                return tlist.map(item => ({
                    id: '' + item.id,
                    serviceName: item.serviceName,
                    serviceAbs: item.serviceAbs,
                    serviceKey: item.serviceKey
                }));
            });

            const clist: any[] = Array.isArray(data.caseList) ? data.caseList : [];
            setCaseList(clist.map(item => {
                const productsPc: any[] = Array.isArray(item.products?.pc) ? item.products.pc : [];
                const productsWise: any[] = Array.isArray(item.products?.wise) ? item.products.wise : [];

                return {
                    id: '' + item.id,
                    caseName: item.caseName,
                    caseCategory: item.caseCategory,
                    logo: item.logo,
                    tip: item.tip,
                    des: item.des,
                    casePath: item.casePath,
                    title: item.title,
                    products: {
                        pc: productsPc,
                        wise: productsWise
                    }
                };
            }));

            setPagination((old) => {
                return {
                    ...old,
                    current: pageData.page ?? 1,
                    pageSize: pageData.pageSize ?? 0,
                    total: pageData.totalCount ?? 0
                };
            });
        }
    }, [pagination, selectedIndustryKey, selectedTechKey, clickType]);
    const getCaseListRef = useLatest(getCaseList);
    useEffect(() => {
        getCaseListRef.current();
    }, [getCaseListRef]);

    const onIndustryClick = useCallback((key: string) => {
        setClickType('industry');
        setSelectedIndustryKey(key);
        getCaseListRef.current({
            industry: key,
            clickType: 'industry'
        });
    }, [getCaseListRef]);

    const onTechClick = useCallback((key: string) => {
        setClickType('tech');
        setSelectedTechKey(key);
        getCaseListRef.current({
            tech: key,
            clickType: 'tech'
        });
    }, [getCaseListRef]);

    const onPageChange = useCallback((page) => {
        setPagination((old) => {
            return {
                ...old,
                current: page
            };
        });
        getCaseListRef.current({
            page
        });
    }, [getCaseListRef]);

    return {
        industryList,
        selectedIndustryKey,
        techList,
        selectedTechKey,
        caseList,
        pagination,

        onIndustryClick,
        onTechClick,
        onPageChange
    };
};
